import { useEffect, useMemo, useRef, useState } from 'react';
import { useDataProvider } from 'react-admin';
import { ExtendedDataProvider } from '../../../types';
import { FilterOption, SelectFilterField } from './types';

const defaultMapOption = (record: any): FilterOption => ({
    value: record.id,
    label: record.name,
    text: record.name,
});

/**
 * Resolves a select field's options: static list, or fetched through
 * dataProvider.getFilterOptions and re-fetched only when this field's own
 * cascade parents change — not on every unrelated filter edit.
 */
const useFilterOptions = (field: SelectFilterField, values: Record<string, any>) => {
    const dataProvider: ExtendedDataProvider = useDataProvider();
    const [fetched, setFetched] = useState<FilterOption[] | null>(null);
    const [loading, setLoading] = useState(false);

    // Only the parents this field declares participate in the query, so an
    // unrelated filter change cannot invalidate these options. Both deps are
    // strings, keeping the dependency array a fixed size.
    const parentKey = (field.dependsOn || []).map((p) => `${p}=${values[p] ?? ''}`).join('&');
    const staticKey = JSON.stringify(field.optionsFilter || {});

    const parentFilter = useMemo(() => {
        const filter: Record<string, any> = { ...(field.optionsFilter || {}) };
        (field.dependsOn || []).forEach((parent) => {
            filter[parent] = values[parent];
        });
        return filter;
        // deps are the serialised parentKey/staticKey, not the raw objects
    }, [parentKey, staticKey]);

    const parentsSatisfied = (field.dependsOn || []).every((parent) => {
        const value = values[parent];
        return value !== undefined && value !== null && value !== '';
    });

    const filterKey = `${parentKey}|${staticKey}`;
    const latestRequest = useRef(0);

    useEffect(() => {
        if (!field.resource || !parentsSatisfied) {
            setFetched(field.resource ? [] : null);
            return;
        }
        const requestId = ++latestRequest.current;
        setLoading(true);
        const query = {
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'name', order: 'ASC' },
            filter: parentFilter,
        };
        const load = field.optionsFrom === 'list'
            ? dataProvider.getList(field.resource, query as any)
            : dataProvider.getFilterOptions(field.resource, query as any);
        load
            .then(({ data }: any) => {
                // ignore a slow response that a newer request has superseded
                if (requestId !== latestRequest.current) return;
                setFetched((data || []).map(field.mapOption || defaultMapOption));
            })
            .catch(() => {
                if (requestId === latestRequest.current) setFetched([]);
            })
            .finally(() => {
                if (requestId === latestRequest.current) setLoading(false);
            });
    }, [field.resource, filterKey, parentsSatisfied, dataProvider]);

    return {
        options: field.options ?? fetched ?? [],
        loading,
        disabled: !parentsSatisfied,
    };
};

export default useFilterOptions;
